import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { flowsheetUrl } from './api';
import { ClinicalEventEntry, type EntryDescriptor } from './ClinicalEventEntry';
import type { DyadData } from './NewbornChart';
import type { PostpartumDyadProps } from './PostpartumDyad';
export function DyadReview({ episode, events, timeZone, disabled, onSave }: PostpartumDyadProps) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const [reviewedHeadSnapshot, setReviewedHeadSnapshot] = useState<string | null>(null);
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [reason, setReason] = useState('');
  const query = useQuery({
    queryKey: ['birth-dyad', orgId, episode.id, events.length, events.at(-1)?.id],
    enabled: !!orgId && navigator.onLine,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === orgId && previousQuery?.queryKey[2] === episode.id ? previous : undefined,
    queryFn: async () => (await clientApi.get<DyadData>(`${flowsheetUrl(orgId!)}/episodes/${episode.id}/dyad`)).data,
  });
  const reviewUnavailable =
    disabled || busy || !navigator.onLine || query.isFetching || query.isError || query.isPlaceholderData;
  const dyad = query.data,
    mother = episode.subjects.find((s) => s.subject_type === 'MOTHER');

  const activeBirths = dyad?.babies.filter((b) => b.active).map((b) => b.birth_head.id) ?? [];
  const currentHeads = [...activeBirths].sort().join(',');
  const changedBirthEvidence = reviewedHeadSnapshot !== null && reviewedHeadSnapshot !== currentHeads;
  const outcomeUnavailable = reviewUnavailable || !activeBirths.length || changedBirthEvidence;
  const bindReview = () => {
    if (reviewedHeadSnapshot === null) setReviewedHeadSnapshot(currentHeads);
  };
  const descriptor: EntryDescriptor = {
    eventType: 'POSTPARTUM.OUTCOME_REVIEW',
    label: 'Reviewed pregnancy outcome',
    initialPayload: { reviewed_birth_head_ids: activeBirths },
    fields: [
      ...['gravida', 'term', 'preterm', 'abortions', 'living'].map((key) => ({
        key: `prior_history.${key}`,
        label: `Prior history excluding this pregnancy: ${key}`,
        kind: 'number' as const,
      })),
      {
        key: 'prior_history_excludes_current_pregnancy',
        label: 'I verified these prior counts exclude the current pregnancy',
        kind: 'boolean',
      },
      {
        key: 'classification',
        label: 'Pregnancy outcome classification',
        options: [
          { value: 'TERM', label: 'Term' },
          { value: 'PRETERM', label: 'Preterm' },
          { value: 'LOSS', label: 'Pregnancy loss' },
        ],
      },
      { key: 'living_newborns', label: 'Number of living newborns from this pregnancy', kind: 'number' },
      { key: 'reason', label: 'Outcome review note', kind: 'textarea' },
    ],
  };
  async function receipt(task: DyadData['tasks'][number]) {
    if (reviewUnavailable || !reason.trim() || !task.subject_id) return;
    setBusy(true);
    setError('');
    try {
      await onSave(
        [
          {
            event_type: 'CARE.TASK',
            schema_version: 2,
            subject_id: task.subject_id,
            payload: {
              task_id: task.id,
              source_event_id: task.source_event_id,
              status: 'COMPLETE',
              note: reason.trim(),
            },
          },
        ],
        new Date().toISOString(),
      );
      setReason('');
      await query.refetch();
    } catch {
      setError(
        'Task review could not be accepted. The note is retained; synchronize and review the current source evidence.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Mother–newborn links and care review</CardTitle>
        <CardDescription>
          These linked records and tasks reflect server-confirmed evidence. Offline entries appear after
          synchronization.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {query.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              Linked care evidence could not be refreshed.{' '}
              <Button variant="outline" onClick={() => query.refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {dyad && (
          <>
            <p>Delivery: {dyad.delivery.status.replaceAll('_', ' ')}</p>
            {dyad.babies.map((b) => (
              <p key={b.fetus_subject_id}>
                {episode.subjects.find((s) => s.id === b.fetus_subject_id)?.label} ·{' '}
                {String(b.birth_head.payload.outcome)} ·{' '}
                {b.newborn_patient_id ? (
                  <Link to="/patients/$patientId" params={{ patientId: b.newborn_patient_id }}>
                    Open newborn chart
                  </Link>
                ) : (
                  'Fetal record retained'
                )}
                {!b.active ? ' · Birth evidence inactive' : ''}
                {b.demographics_need_review ? ' · Newborn demographic review needed' : ''}
              </p>
            ))}
            {dyad.delivery.gtpal ? (
              <p>
                Reviewed obstetric totals:{' '}
                {Object.entries(dyad.delivery.gtpal)
                  .map(([k, v]) => `${k} ${v}`)
                  .join(' · ')}
              </p>
            ) : (
              <p>
                Obstetric totals need review of prior history and all birth outcomes. Prior pregnancy counts have not
                been overwritten.
              </p>
            )}
            {changedBirthEvidence && (
              <Alert>
                <AlertDescription>
                  Birth evidence changed. Your draft fields are retained. Re-read the current outcomes before attaching
                  the review to the updated evidence.{' '}
                  <Button
                    variant="outline"
                    disabled={reviewUnavailable || !activeBirths.length}
                    onClick={() => setReviewedHeadSnapshot(currentHeads)}>
                    I reviewed the updated birth evidence
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {mother && (
              <div onInputCapture={bindReview} onClickCapture={bindReview}>
                <ClinicalEventEntry
                  title="Review obstetric totals"
                  descriptors={[descriptor]}
                  subjects={[mother]}
                  timeZone={timeZone}
                  disabled={outcomeUnavailable}
                  onSave={async (entries, at) => {
                    if (outcomeUnavailable)
                      throw new Error('Refresh the current birth evidence before recording the review.');
                    await onSave(entries, at);
                    setReviewedHeadSnapshot(null);
                    await query.refetch();
                  }}
                />
              </div>
            )}
            {!!dyad.tasks.length && (
              <>
                <FieldGroup>
                  <Field>
                    <FieldLabel>Care-task review note</FieldLabel>
                    <Textarea
                      aria-label="Care-task review note"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={disabled || busy}
                    />
                  </Field>
                </FieldGroup>
                {dyad.tasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between gap-4">
                    <span>
                      {task.title ?? task.label} · {String(task.status ?? 'OPEN')}
                    </span>
                    <Button
                      variant="outline"
                      className="min-h-11"
                      disabled={reviewUnavailable || !reason.trim() || task.status === 'COMPLETE'}
                      onClick={() => receipt(task)}>
                      Record review completed
                    </Button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
